// The owner's "I can still copy the link I just made" state: the tab-scoped stash,
// the clipboard write, and the brief confirmation that the write happened.
//
// All three live together because they are one affordance, and putting them in a
// hook rather than in `CopyLinksPanel` buys three specific things:
//
//   1. `CopyLinksPanel` stays pure-props (components/ convention) — it receives a
//      `copyId`, an `onCopy` and a `justCopied` flag, and reads no storage.
//   2. AppInner gains a hook CALL and no `useState` — the ratchet's
//      `appInnerUseState` counter is a hard CI gate.
//   3. THE CLIPBOARD WRITE BECOMES TESTABLE. This is the one that changed a
//      decision: with the write inlined in the component, mutating it to copy the
//      wrong string was an unkillable mutant at unit level (a static render cannot
//      be clicked), and the property "the button copies the FULL URL, secret
//      included" is the most consequential thing on this surface. Behind an
//      injectable `writeClipboard`, a hook test calls `copy()` and reads the exact
//      argument. The e2e still covers the real click on the real clipboard.
//
// WHAT MUST NEVER HAPPEN TO THE VALUE (mintedStash.js rule 4, restated where the
// value is actually handled): the URL carries the live secret in its fragment. It
// must not reach a log, a query string, the address bar, an analytics call, or the
// rendered markup. This hook hands it to exactly one place — `writeClipboard` —
// and exposes `copyId` (a non-secret handle) for anything that needs to identify
// the link.
//
// IT NOW ALSO EXPOSES `url`, AND THE PANEL STILL MUST NOT RENDER ONE. Withholding
// the string used to make "the panel cannot render the URL" structural — a fact
// about its props rather than about its behaviour. That structural guarantee is
// gone, because `selectCurrentLink` needs the string to hand to the clipboard, so
// the property is now "received, never rendered" and is held by the render body
// plus the test that pins it against rows carrying real URLs.
//
// Do not read this as permission to display it. The URL was visible for exactly one
// commit (366975e51) before the product owner replaced it with the copy id; a link
// is represented by its id everywhere on this surface. Anything that prints this
// string puts a live capability into the DOM, and from there into screenshots and
// bug bundles. `hasUrl` stays for callers that only need the boolean.
//
// A STASH THAT FAILS DEGRADES TO THE OLD BEHAVIOUR, NOT TO A BROKEN PANEL. In a
// sandboxed frame, in Safari private mode, or on a full quota, `stashMintedUrl`
// returns false; `remember()` then keeps the value in ordinary React state, so the
// affordance works for this page load and simply does not survive a reload — which
// is precisely where this feature started.
import { useCallback, useEffect, useState } from "react";
import { copyToClipboard } from "../lib/util/clipboard.js";
import {
  clearMintedUrl,
  isCapabilityUrl,
  MINTED_STASH_TTL_MS,
  readMintedUrl,
  stashMintedUrl,
} from "../lib/copyLinks/mintedStash.js";

/** How long the copy result stays on screen. Long enough to read, short enough not
 *  to be mistaken for a persistent state. Shared by both outcomes. */
export const COPIED_FLASH_MS = 2000;

/**
 * The most recently minted copy-link URL for one forecast, and the one action that
 * may touch it.
 *
 * @param {object} args
 * @param {string|null|undefined} args.forecastId  the SERVER uuid. Falsy is a
 *   supported state (nothing stashed, nothing offered).
 * @param {object|null} [args.storage]  injectable store; omit for `sessionStorage`.
 *   `null` means "no storage", which is how a test exercises the degraded path.
 * @param {Function} [args.nowFn]  injectable clock, for the TTL.
 * @param {Function} [args.writeClipboard]  injectable clipboard write. Must answer
 *   `true` (or a promise of it) on success; anything else counts as a failure.
 * @returns {{
 *   copyId: string|null, url: string|null, hasUrl: boolean, persisted: boolean,
 *   justCopied: boolean, copyFailed: boolean,
 *   remember: (minted: object|null) => boolean,
 *   forget: () => void,
 *   forgetIf: (copyId: unknown) => void,
 *   copy: (urlOverride?: string) => Promise<boolean>,
 * }}
 *   `copyId` — which link the held URL belongs to, so a caller can put the
 *   affordance on the right row. `hasUrl` — whether there is anything to copy.
 *   `persisted` — the URL survived into storage (false means memory-only).
 *   `justCopied` / `copyFailed` — mutually exclusive, both false at rest.
 *   `copy(urlOverride)` — the server returns a `url` per row to the link's
 *   owner, and an override WINS over the stash: the stash holds only what this
 *   tab minted, the server holds every live link. Omit it (or pass an empty
 *   string) to fall back to the stash, which covers the window between a mint
 *   and the list read that carries its URL back.
 */
export function useMintedLinkStash({
  forecastId,
  storage,
  nowFn = Date.now,
  writeClipboard = copyToClipboard,
} = {}) {
  // The DEGRADED-PATH fallback, and nothing else. Storage is the primary; this is
  // what keeps the affordance working when `stashMintedUrl` refuses.
  //
  // It has to RE-IMPLEMENT the two properties storage gets for free from its key
  // and its stored expiry, and a test caught the second one missing: without
  // `forecastId` a fallback value shows up on a different forecast, and without
  // `expiresAt` the TTL is bypassed for the whole life of the page — not only in a
  // refusing browser, since `remember` populates this on the happy path too. A
  // capability that outlives its own expiry because it happens to also be in
  // memory is exactly the leak the TTL exists to prevent.
  const [memo, setMemo] = useState(null);
  // The last copy ATTEMPT and its outcome — `{ at, ok }` or null. One piece of
  // state rather than two flags, so "succeeded" and "failed" cannot both be on
  // screen: a second attempt replaces the first outcome instead of racing it.
  const [flash, setFlash] = useState(null);

  // Read on EVERY render rather than through a memo: an entry can expire between
  // renders, and a memo keyed on anything but the clock would keep serving a dead
  // capability. The read is a `getItem` plus a small `JSON.parse`.
  const now = nowFn();
  const stashed = readMintedUrl(forecastId, { storage, now });
  const fallback = memo && memo.forecastId === forecastId && now < memo.expiresAt ? memo : null;
  const entry = stashed || fallback;

  // The result clears itself. The `setFlash` is inside the timer callback, not in
  // the effect body — `react-hooks/set-state-in-effect` is an ERROR in this config
  // and the baseline is 0 errors. `flash` is a fresh object per attempt, so a
  // second copy re-arms the timer rather than inheriting the first one's remainder.
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), COPIED_FLASH_MS);
    return () => clearTimeout(t);
  }, [flash]);

  const remember = useCallback(
    (minted) => {
      // THE SAME GATE STORAGE USES, applied before either write. Without it the
      // two paths disagreed: `stashMintedUrl` validates, `setMemo` did not, so a
      // mint body with no usable URL was rejected on a normal browser and held in
      // memory on one that refuses storage. Mutation testing surfaced it as an
      // unkilled `!entry.url` guard in `copy()` — the guard was live precisely
      // because this gate was missing.
      if (!forecastId || !minted) return false;
      if (typeof minted.copyId !== "string" || !minted.copyId) return false;
      if (!isCapabilityUrl(minted.url)) return false;
      const at = nowFn();
      const ok = stashMintedUrl(forecastId, minted, { storage, now: at });
      // Held in memory whether or not storage took it: on the success path the two
      // agree and the storage read wins; on the failure path this is the only copy
      // that exists, and dropping it would be strictly worse than before this hook.
      // The SAME expiry either way — see the note on `memo` above.
      setMemo({
        forecastId,
        copyId: minted.copyId,
        url: minted.url,
        expiresAt: at + MINTED_STASH_TTL_MS,
      });
      return ok;
    },
    // `setMemo` is listed although React guarantees a setter is identity-stable:
    // the React Compiler's `preserve-manual-memoization` rule infers it and treats
    // the mismatch as an ERROR, and the eslint baseline is 0 errors. Same below.
    [forecastId, storage, nowFn, setMemo],
  );

  const forget = useCallback(() => {
    clearMintedUrl(forecastId, { storage });
    setMemo(null);
  }, [forecastId, storage, setMemo]);

  // Guarded by identity rather than unconditional, because revoking a DIFFERENT
  // link must not throw away a URL that is still good. The comparison is against
  // whatever is currently held, storage or memory.
  // No truthiness guard on `copyId`: with nothing held, `held` is null and
  // `forget()` is already a no-op (there is no stored entry to clear and `memo` is
  // already null), so `copyId && …` was mutation-verified as semantically
  // equivalent dead code.
  const held = entry ? entry.copyId : null;
  const forgetIf = useCallback(
    (copyId) => {
      if (copyId === held) forget();
    },
    [held, forget],
  );

  // `urlOverride` is the server-held URL for a row (`GET /api/copy-links` now
  // returns one per link for its owner). When present it WINS over the stash:
  // the stash holds only what this tab minted, while the server holds every
  // live link the owner has, so preferring the stash would offer a stale URL
  // for one row and nothing for the others. The stash remains the fallback for
  // the window between a mint and the list read that would carry its URL back.
  const copy = useCallback(
    async (urlOverride) => {
      // No `!entry.url` companion: `remember` and `readMintedUrl` now apply the same
      // `isCapabilityUrl` gate, so an entry without a usable URL cannot exist. The
      // extra guard was mutation-verified as unreachable once that gate landed —
      // the same finding as the dropped `revokingId != null` and `expiresAt !== ""`
      // guards elsewhere in this feature.
      // A usable URL from either source. Guarded on the STRING, not on `entry`,
      // because an override arrives for rows this tab never minted and so has no
      // entry for — the common case now that the server returns them.
      const url = typeof urlOverride === "string" && urlOverride ? urlOverride : entry?.url;
      if (!url) return false;
      // AWAITED, AND THE CONFIRMATION FOLLOWS THE ANSWER. `copyToClipboard` used to
      // return nothing, so this reported success unconditionally — on an insecure
      // origin (the API is secure-context only) the user was told "full link
      // copied" when nothing was. For a copy link that is the expensive lie: they
      // believe they captured a capability that cannot be shown again.
      // WRAPPED, even though `copyToClipboard` is documented never to reject: this is
      // an injectable seam, `copy()` is wired straight to an onClick that discards the
      // promise, and a rejection escaping here would surface as an unhandled rejection
      // in the browser with NEITHER outcome shown — the user left looking at an
      // affordance that did nothing and said nothing. A throw is a failed copy; there
      // is no third answer to give.
      //
      // STRICT `=== true`, so anything that is not an explicit success — a clipboard
      // impl that returns undefined, a stub that forgets to answer — reads as failure.
      // Fail-closed is the right direction for a confirmation.
      let ok = false;
      try {
        ok = (await writeClipboard(url)) === true;
      } catch {
        ok = false;
      }
      setFlash({ at: nowFn(), ok });
      return ok;
    },
    // `entry?.url`, not `entry`. `readMintedUrl` allocates a fresh object every
    // render (and so does the `memo`-derived fallback), so depending on `entry`
    // meant the comparison ALWAYS failed and this callback was rebuilt on every
    // render of the shell — the memo cost without the memo. The body reads exactly
    // one field off it, a string, which compares by value and makes this stable.
    [entry?.url, writeClipboard, nowFn, setFlash],
  );

  return {
    copyId: entry ? entry.copyId : null,
    url: entry ? entry.url : null,
    hasUrl: Boolean(entry && entry.url),
    justCopied: flash?.ok === true,
    copyFailed: flash?.ok === false,
    persisted: Boolean(stashed),
    remember,
    forget,
    forgetIf,
    copy,
  };
}
